import { useState } from 'react';
import {
  createAssignee,
  deleteAssignee,
  updateAssignee,
  type Assignee,
  type AssigneeInput,
} from '../../lib/assignees';
import { Alert, Button, Field, Input, Textarea } from '../ui';
import { useToast } from '../toast';

type FormState = { name: string; email: string; role: string; notes: string };

const EMPTY_FORM: FormState = { name: '', email: '', role: '', notes: '' };

/**
 * Create or edit one assignee. Modelled on ProjectForm: a single form-state
 * object, only the required field validated here, and everything else left to
 * the server.
 *
 * Never fetches: create mode has nothing to fetch, and edit mode is handed the
 * already-loaded record.
 */
export function AssigneeForm({
  assignee,
  onSaved,
  onCancel,
  onDeleted,
}: {
  /** Absent = create mode: no archive action and no delete button. */
  assignee?: Assignee;
  onSaved: (assignee: Assignee) => void;
  onCancel: () => void;
  onDeleted?: () => void;
}) {
  const [form, setForm] = useState<FormState>(() =>
    assignee
      ? {
          name: assignee.name,
          email: assignee.email ?? '',
          role: assignee.role ?? '',
          notes: assignee.notes ?? '',
        }
      : EMPTY_FORM,
  );
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const taskCount = assignee?.taskCount ?? 0;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    const name = form.name.trim();
    if (!name) {
      setNameError('Give this person a name.');
      return;
    }

    setSaving(true);
    setError(null);
    setNameError(null);

    // Blank optional fields go as null, not "": the server's unique index would
    // otherwise treat two blank emails as the same address.
    const payload: AssigneeInput = {
      name,
      email: form.email.trim() || null,
      role: form.role.trim() || null,
      notes: form.notes.trim() || null,
    };

    try {
      const saved = assignee
        ? await updateAssignee(assignee.id, payload)
        : await createAssignee(payload);
      // The caller navigates back to the list on save, so this is the only
      // thing that says the record made it.
      toast(assignee ? `Saved ${saved.assignee.name}` : `Added ${saved.assignee.name}`);
      onSaved(saved.assignee);
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save this person.');
    } finally {
      setSaving(false);
    }
  }

  async function setArchived(archived: boolean) {
    if (!assignee) return;
    setSaving(true);
    setError(null);
    try {
      const { assignee: saved } = await updateAssignee(assignee.id, { archived });
      toast(archived ? `Archived ${saved.name}` : `Restored ${saved.name}`);
      onSaved(saved);
    } catch (archiveError: unknown) {
      setError(archiveError instanceof Error ? archiveError.message : 'Could not do that.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!assignee) return;
    setSaving(true);
    try {
      await deleteAssignee(assignee.id);
      toast(`Deleted ${assignee.name}`);
      onDeleted?.();
    } catch (deleteError: unknown) {
      setError(deleteError instanceof Error ? deleteError.message : 'Could not delete this person.');
      setSaving(false);
    }
  }

  return (
    <form className="space-y-5" onSubmit={(event) => void handleSubmit(event)}>
      {error ? <Alert>{error}</Alert> : null}

      {assignee?.archived ? (
        <Alert tone="warning" title="Archived">
          They stay on the {taskCount === 1 ? 'task' : 'tasks'} they already hold, but cannot be
          assigned anything new.
        </Alert>
      ) : null}

      <Field label="Name" htmlFor="assignee-name" error={nameError}>
        <Input
          id="assignee-name"
          autoFocus
          maxLength={120}
          value={form.name}
          placeholder="Who is it?"
          onChange={(event) => {
            setForm({ ...form, name: event.target.value });
            if (nameError) setNameError(null);
          }}
        />
      </Field>

      <Field
        label="Email"
        htmlFor="assignee-email"
        hint="For your reference only. Nothing is sent to it, and it grants no access to Hive."
      >
        <Input
          id="assignee-email"
          type="email"
          maxLength={200}
          value={form.email}
          placeholder="optional"
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />
      </Field>

      <Field label="Role" htmlFor="assignee-role">
        <Input
          id="assignee-role"
          maxLength={80}
          value={form.role}
          placeholder="Contractor, designer, client…"
          onChange={(event) => setForm({ ...form, role: event.target.value })}
        />
      </Field>

      <Field label="Notes" htmlFor="assignee-notes">
        <Textarea
          id="assignee-notes"
          rows={4}
          maxLength={4000}
          value={form.notes}
          placeholder="Anything worth remembering about working with them"
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
        />
      </Field>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 pt-5">
        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving…' : assignee ? 'Save changes' : 'Add assignee'}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>

        {assignee ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={saving} onClick={() => void setArchived(!assignee.archived)}>
              {assignee.archived ? 'Restore' : 'Archive'}
            </Button>

            {/* Inline rather than window.confirm, which blocks the page -- the
                same choice ProjectForm makes. The task count is stated before
                the click, not reported after it. */}
            {confirmingDelete ? (
              <>
                <span className="text-sm text-stone-600">
                  Delete permanently?
                  {/* Not "become unassigned": a task with other people on it
                      does not, so this says what actually happens. */}
                  {taskCount > 0
                    ? ` ${taskCount} ${taskCount === 1 ? 'task loses' : 'tasks lose'} them.`
                    : ''}
                </span>
                <Button variant="danger" disabled={saving} onClick={() => void handleDelete()}>
                  Delete
                </Button>
                <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
                Delete
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </form>
  );
}
